import { Permissions } from '@restore/permission-catalog';

type ScopeChecker = (scopeKey: string) => boolean;

/**
 * Document-form create/update gating (Segment B migration — was
 * `permissionData.includes('Create Eval' | 'Update Eval' | 'Create Recertification' |
 * 'Update Recertification')` off `src/paths.ts` in `view-documents-list.tsx` and
 * `document-manegment.tsx`).
 *
 * A therapist may only add / edit an Evaluation or Recertification form when they hold the
 * matching scopeKey. The catalog display names equal the legacy paths.ts strings
 * (parity-verified in rbac-parity.test.ts), so behavior is preserved — only the mechanism
 * moves from human-name `includes()` to scopeKey `has()`, which is what emits RUM telemetry.
 *
 * Form types are RESTORE backend enum values (e.g. `EVALUATION`, `RECERTIFICATION`,
 * `TRANSITIONAL_EVALUATION_REFORM_PT`). We match by substring exactly as the legacy code did.
 * A form type that is neither an eval nor a recert is not gated by these permissions, so the
 * predicates default to permitted for it (the caller's other branches decide visibility).
 */

const isRecertFormType = (formType: string | undefined): boolean =>
  !!formType?.includes('RECERTIFICATION');

const isEvalFormType = (formType: string | undefined): boolean =>
  !!formType?.includes('EVALUATION');

/**
 * May this user CREATE (add a new document from) the given form template type?
 * Mirrors the template-`.filter()` gate: recert templates need Create-Recertification,
 * evaluation templates need Create-Eval; anything else is not permission-gated here.
 */
export const canCreateFormType = (has: ScopeChecker, formType: string | undefined): boolean => {
  if (isRecertFormType(formType)) return has(Permissions.CREATE_RECERTIFICATION);
  if (isEvalFormType(formType)) return has(Permissions.CREATE_EVAL);
  return true;
};

/**
 * May this user EDIT the given form type on permission grounds?
 * Mirrors the `checkEditPermission` legacy fallback: recert forms need Update-Recertification,
 * evaluation forms need Update-Eval; anything else is not permission-gated here.
 */
export const canUpdateFormType = (has: ScopeChecker, formType: string | undefined): boolean => {
  if (isRecertFormType(formType)) return has(Permissions.UPDATE_RECERTIFICATION);
  if (isEvalFormType(formType)) return has(Permissions.UPDATE_EVAL);
  return true;
};
